import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Defaults, moment, useInterval } from '@pbt/pbt-ui-components'

import { DEFAULT_CONVERSATION } from '~/constants/communications'
import { clearFilesToSend } from '~/store/actions/conversationMessages'
import {
  fetchConversation,
  fetchMoreItemsForConversationMessagesList,
} from '~/store/actions/conversations'
import { getConstantsIsLoading } from '~/store/reducers/constants'
import { getChatMessages } from '~/store/reducers/conversationMessages'
import { getConversationById } from '~/store/reducers/conversations'

export function useConversationChat({
  conversationId,
  targetMessageId,
  isInitialized = false,
}: {
  conversationId: string
  isInitialized?: boolean
  targetMessageId?: string | null
}) {
  const dispatch = useDispatch()
  const conversation =
    useSelector(getConversationById(conversationId)) || DEFAULT_CONVERSATION
  const isConstantsLoading = useSelector(getConstantsIsLoading)

  const { totalCount } = conversation
  const messages = useSelector(
    getChatMessages(conversation.messages || []),
  ).sort((a, b) => moment(a.timestamp).diff(b.timestamp))

  const loadMoreItems = useCallback(() => {
    const from = messages.length
    const to = messages.length + Defaults.CONVERSATION_MESSAGES_BATCH_LOAD_COUNT
    dispatch(
      fetchMoreItemsForConversationMessagesList(from, to, conversationId),
    )
  }, [dispatch, messages.length, conversationId])

  // Clear files when switching conversations (only on conversationId change)
  useEffect(() => {
    dispatch(clearFilesToSend())
  }, [conversationId])

  // Fetch conversation data if needed
  useEffect(() => {
    if (isConstantsLoading) {
      return
    }

    // Only fetch if we don't have conversation data yet
    // Check if conversation.id matches conversationId to ensure it's not DEFAULT_CONVERSATION
    const hasConversationData =
      'id' in conversation &&
      conversation.id === conversationId &&
      conversation.totalCount !== undefined

    if (hasConversationData) {
      return
    }

    // When navigating to a specific message, fetch metadata only (skipMessages=true)
    // Chat.tsx will use loadMoreItems() to find the target message
    // Otherwise, fetch conversation normally (which may also fetch initial messages)
    const skipMessages = Boolean(targetMessageId)
    dispatch(fetchConversation(conversationId, skipMessages))
  }, [
    conversationId,
    targetMessageId,
    isConstantsLoading,
    conversation,
    dispatch,
  ])

  useInterval(() => {
    // Skip interval fetch during initial loading only when navigating to a target message
    // to avoid disrupting scroll position. For normal conversation loads, allow interval
    // to proceed to handle cases where initial fetch may be slow.
    if (isConstantsLoading || (targetMessageId && !isInitialized)) {
      return
    }
    dispatch(fetchConversation(conversationId))
  }, Defaults.CONVERSATION_MESSAGES_UPDATE_INTERVAL)

  return { loadMoreItems, messages, totalCount }
}
